/**
 * Profile / My Account Page
 * Self-service account management
 */

import React, { useState, useEffect } from 'react';
import {
  User,
  Envelope,
  Phone,
  Lock,
  Calendar,
  Shield,
  Eye,
  EyeSlash,
  CheckCircle,
  FloppyDisk,
  ArrowsClockwise,
  Warning,
  Info,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { profileApi } from '../utils/authApi';
import { formatRelativeDate } from '../utils/formatters';

// Role badge colors
const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  user: 'bg-blue-100 text-blue-700 border-blue-200',
  analyst: 'bg-amber-100 text-amber-700 border-amber-200',
  viewer: 'bg-stone-100 text-stone-700 border-stone-200',
  partner: 'bg-emerald-100 text-emerald-700 border-emerald-200',
};

export const Profile = () => {
  const { user, refreshUser, isAdmin } = useAuth();
  
  // Profile form state
  const [profileData, setProfileData] = useState({
    full_name: '',
    email: '',
    phone: '',
  });
  const [profileErrors, setProfileErrors] = useState({});
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  
  // Password form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    new_password_confirm: '',
  });
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        email: user.email || '',
        phone: user.phone || '',
      });
    }
  }, [user]);

  // Validate profile form
  const validateProfile = () => {
    const errors = {};
    
    if (!profileData.full_name?.trim()) {
      errors.full_name = 'Le nom est requis';
    } else if (profileData.full_name.trim().length < 2) {
      errors.full_name = 'Le nom doit avoir au moins 2 caractères';
    }
    
    if (!profileData.email?.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(profileData.email)) {
      errors.email = 'Format d\'email invalide';
    }
    
    setProfileErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validate password form
  const validatePassword = () => {
    const errors = {};
    
    if (!passwordData.current_password) {
      errors.current_password = 'Le mot de passe actuel est requis';
    }
    
    if (!passwordData.new_password) {
      errors.new_password = 'Le nouveau mot de passe est requis';
    } else if (passwordData.new_password.length < 8) {
      errors.new_password = 'Le mot de passe doit avoir au moins 8 caractères';
    } else if (!/[A-Z]/.test(passwordData.new_password)) {
      errors.new_password = 'Le mot de passe doit contenir une majuscule';
    } else if (!/[a-z]/.test(passwordData.new_password)) {
      errors.new_password = 'Le mot de passe doit contenir une minuscule';
    } else if (!/\d/.test(passwordData.new_password)) {
      errors.new_password = 'Le mot de passe doit contenir un chiffre';
    }
    
    if (passwordData.new_password !== passwordData.new_password_confirm) {
      errors.new_password_confirm = 'Les mots de passe ne correspondent pas';
    }
    
    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle profile update
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    if (!validateProfile()) return;
    
    setIsUpdatingProfile(true);
    try {
      await profileApi.update({
        full_name: profileData.full_name,
        email: profileData.email,
        phone: profileData.phone || null,
      });
      await refreshUser();
      toast.success('Profil mis à jour avec succès');
    } catch (err) {
      const message = err.response?.data?.detail || 'Échec de la mise à jour du profil';
      toast.error(message);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  // Handle password change
  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) return;
    
    setIsChangingPassword(true);
    try {
      await profileApi.changePassword(
        passwordData.current_password,
        passwordData.new_password,
        passwordData.new_password_confirm
      );
      toast.success('Mot de passe modifié avec succès');
      setPasswordData({
        current_password: '',
        new_password: '',
        new_password_confirm: '',
      });
    } catch (err) {
      const message = err.response?.data?.detail || 'Échec du changement de mot de passe';
      toast.error(message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Get initials
  const initials = user?.full_name 
    ? user.full_name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase()
    : '?';

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in max-w-4xl mx-auto" data-testid="profile-page">
      {/* Header - Mobile optimized */}
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-[#3A4A40] text-white flex items-center justify-center text-lg sm:text-2xl font-bold flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-lg sm:text-2xl font-bold truncate">{user.full_name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className={cn('text-xs', ROLE_COLORS[user.role])}>
              {user.role}
            </Badge>
            <Badge variant="secondary" className="text-xs bg-emerald-100 text-emerald-700">
              <CheckCircle size={12} className="mr-1" />
              {user.status}
            </Badge>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Profile Information */}
        <Card data-testid="profile-info-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <User size={20} />
              Informations du Profil
            </CardTitle>
            <CardDescription>
              Mettre à jour vos informations de compte
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleUpdateProfile} className="space-y-4">
              {/* Full Name */}
              <div className="space-y-2">
                <Label htmlFor="full_name">Nom Complet</Label>
                <Input
                  id="full_name"
                  value={profileData.full_name}
                  onChange={(e) => setProfileData(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Votre nom complet"
                  className={profileErrors.full_name ? 'border-red-500' : ''}
                  data-testid="input-profile-name"
                />
                {profileErrors.full_name && (
                  <p className="text-xs text-red-500">{profileErrors.full_name}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Envelope 
                    size={16} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                  />
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="votre@email.com"
                    className={cn('pl-9', profileErrors.email ? 'border-red-500' : '')}
                    data-testid="input-profile-email"
                  />
                </div>
                {profileErrors.email && (
                  <p className="text-xs text-red-500">{profileErrors.email}</p>
                )}
              </div>

              {/* Phone */}
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone (optionnel)</Label>
                <div className="relative">
                  <Phone 
                    size={16} 
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" 
                  />
                  <Input
                    id="phone"
                    value={profileData.phone}
                    onChange={(e) => setProfileData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+33 6 12 34 56 78"
                    className="pl-9"
                    data-testid="input-profile-phone"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#3A4A40] hover:bg-[#2E3B33]"
                disabled={isUpdatingProfile}
                data-testid="save-profile-btn"
              >
                {isUpdatingProfile ? (
                  <>
                    <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <FloppyDisk size={16} className="mr-1" />
                    Enregistrer
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Change Password */}
        <Card data-testid="change-password-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Lock size={20} />
              Changer le Mot de Passe
            </CardTitle>
            <CardDescription>
              Mettez à jour votre mot de passe régulièrement pour la sécurité
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleChangePassword} className="space-y-4">
              {/* Current Password */}
              <div className="space-y-2">
                <Label htmlFor="current_password">Mot de Passe Actuel</Label>
                <div className="relative">
                  <Input
                    id="current_password"
                    type={showPasswords ? 'text' : 'password'}
                    value={passwordData.current_password}
                    onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                    placeholder="••••••••"
                    className={cn('pr-10', passwordErrors.current_password ? 'border-red-500' : '')}
                    data-testid="input-current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  >
                    {showPasswords ? <EyeSlash size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {passwordErrors.current_password && (
                  <p className="text-xs text-red-500">{passwordErrors.current_password}</p>
                )}
              </div>

              {/* New Password */}
              <div className="space-y-2">
                <Label htmlFor="new_password">Nouveau Mot de Passe</Label>
                <Input
                  id="new_password"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                  placeholder="Min. 8 car., 1 maj., 1 min., 1 chiffre"
                  className={passwordErrors.new_password ? 'border-red-500' : ''}
                  data-testid="input-new-password"
                />
                {passwordErrors.new_password && (
                  <p className="text-xs text-red-500">{passwordErrors.new_password}</p>
                )}
              </div>

              {/* Confirm New Password */}
              <div className="space-y-2">
                <Label htmlFor="new_password_confirm">Confirmer le Nouveau Mot de Passe</Label>
                <Input
                  id="new_password_confirm"
                  type={showPasswords ? 'text' : 'password'}
                  value={passwordData.new_password_confirm}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, new_password_confirm: e.target.value }))}
                  placeholder="Ressaisir le nouveau mot de passe"
                  className={passwordErrors.new_password_confirm ? 'border-red-500' : ''}
                  data-testid="input-confirm-password"
                />
                {passwordErrors.new_password_confirm && (
                  <p className="text-xs text-red-500">{passwordErrors.new_password_confirm}</p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-[#3A4A40] hover:bg-[#2E3B33]"
                disabled={isChangingPassword}
                data-testid="change-password-btn"
              >
                {isChangingPassword ? (
                  <>
                    <ArrowsClockwise size={16} className="mr-1 animate-spin" />
                    Modification...
                  </>
                ) : (
                  <>
                    <Lock size={16} className="mr-1" />
                    Changer le Mot de Passe
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Account Information */}
      <Card data-testid="account-info-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Info size={20} />
            Informations du Compte
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Rôle</p>
              <Badge variant="outline" className={cn('text-xs sm:text-sm', ROLE_COLORS[user.role])}>
                <Shield size={14} className="mr-1 hidden sm:inline" />
                {user.role}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Statut</p>
              <Badge variant="secondary" className="text-xs sm:text-sm bg-emerald-100 text-emerald-700">
                <CheckCircle size={14} className="mr-1 hidden sm:inline" />
                {user.status}
              </Badge>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Dernière Connexion</p>
              <p className="text-xs sm:text-sm flex items-center gap-1">
                <Calendar size={14} className="text-muted-foreground hidden sm:inline" />
                {user.last_login_at 
                  ? formatRelativeDate(user.last_login_at)
                  : 'Jamais'
                }
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Membre Depuis</p>
              <p className="text-xs sm:text-sm flex items-center gap-1">
                <Calendar size={14} className="text-muted-foreground hidden sm:inline" />
                {user.created_at 
                  ? formatRelativeDate(user.created_at)
                  : 'Inconnu'
                }
              </p>
            </div>
          </div>
          
          {isAdmin && (
            <div className="mt-4 p-3 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center gap-2">
                <Shield size={16} className="text-purple-600" />
                <span className="text-sm text-purple-700">
                  Vous avez les privilèges administrateur. Vous pouvez gérer les utilisateurs dans la page Gestion Utilisateurs.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Profile;
